import { ProductRange, Store } from "../../../../types";
import { getProductRangeReport } from "../../../../lib/admin";
import { useReportPage } from "../hooks/useReportPage";
import StoreReportLoadingRows from '../shared/StoreReportLoadingRows';
import StoreReportRow from '../shared/StoreReportRow';
import StoreReportRowHeader from '../shared/StoreReportRowHeader';
import StoreReportRangeDetails from './StoreReportRangeDetails';
import StoreReportToolbar from '../StoreReportToolbar';
import { EntityLabel } from '../ForceSyncSelectedButton';
import StoreReportNoResultsMessage from '../StoreReportNoResultsMessage';
import Notification from '../../../global/Notification';
import Pagination from '../../../global/Pagination';

const RANGES_PER_PAGE = 25;
const MAX_SELECTED_RANGES = 100;
const ENTITY_LABEL: EntityLabel = { one: "product range", many: "product ranges" };

type Props =
    {
        store: Store
        onForceSyncAll?: () => Promise<string>
        onForceSyncSelection?: (entityIds: number[]) => Promise<string>
        isSyncing?: boolean
    }

export default function StoreReportRangesList({ store, onForceSyncAll, onForceSyncSelection, isSyncing }: Props) {
    const report = useReportPage<ProductRange>({
        storeId: store.id,
        pageSize: RANGES_PER_PAGE,
        maxSelected: MAX_SELECTED_RANGES,
        selectionLimitNotice: `You can select up to ${MAX_SELECTED_RANGES} ${ENTITY_LABEL.many} at a time.`,
        fetchPage: async (page, pageSize, search, status) => {
            const result = await getProductRangeReport(store.id, page, pageSize, search, status);
            return { rows: result.productRanges ?? [], total: result.pagination?.total ?? 0 };
        },
        isSyncing,
        onForceSyncSelection,
    });

    const openInShopify = (shopifyId: string) => {
        const storeSplit = store.storeUrl.split('.');
        const storeURL = storeSplit[0];
        window.open(`https://admin.shopify.com/store/${storeURL}/collections/${shopifyId}`, '_blank');
    }

    return (
        <div className="products products-report">
            <StoreReportToolbar
                search={report.search}
                setSearch={report.updateSearch}
                status={report.status}
                setStatus={report.updateStatus}
                onRefresh={report.refresh}
                isLoading={report.isLoading}
                onForceSyncAll={onForceSyncAll}
                onForceSyncSelected={onForceSyncSelection && report.forceSyncSelection}
                selectedCount={report.selectedIds.length}
                entityLabel={ENTITY_LABEL}
                onClearSelection={report.clearSelection}
                isSyncing={isSyncing}
            />
            {!report.retrieveError && <StoreReportRowHeader allSelected={report.allOnPageSelected} onToggleAll={report.toggleAllOnPage} />}
            {report.isLoading ? (
                <StoreReportLoadingRows />
            ) : report.retrieveError ? (
                <p className="sync-error-retrieve">Currently unable to retrieve the information. Please try again later.</p>
            ) : report.rows.length === 0 ? (
                report.search || report.status ? (
                    <StoreReportNoResultsMessage
                        reportName="Product Ranges"
                        searchTerm={report.search}
                        onClear={report.clearFilters}
                    />
                ) : (
                    <p className="no-products-message">No product ranges available to display</p>
                )
            ) : (
                <div>
                    {report.rows.map((productRange) => (
                        <div key={productRange.id} className="products-list-view">
                            <StoreReportRow
                                row={productRange}
                                openInShopify={openInShopify}
                                selected={report.selectedIds.includes(productRange.id)}
                                onToggleSelected={report.toggleSelected}
                                isExpanded={report.expandedId === productRange.id}
                                onToggleDetails={report.toggleDetails}
                                renderDetails={() => <StoreReportRangeDetails storeId={store.id} productRange={productRange} />}
                            ></StoreReportRow>
                        </div>
                    ))}

                    <Pagination
                        pageSize={RANGES_PER_PAGE}
                        totalItems={report.total}
                        paginate={report.setCurrentPage}
                        currentPage={report.currentPage}
                    />
                </div>
            )}
            <Notification message={report.selectionNotice} type="error" onClose={report.dismissNotice} />
        </div>
    );
}